import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { buildUrl } from '@trimble-gcs/common';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { ConnectRegionService } from '../connect/connect-region.service';
import { injectLogger } from '../logging/logger-injection';
import { SettingService } from '../setting/setting.service';
import { LegacyConnectView, ReservedConnectView, ViewData } from './host-3d.model';

const RESERVED_VIEW_APP = 'TRCPS';
const LEGACY_VIEW_SETTING_PREFIX = 'view_';

@Injectable({
  providedIn: 'root',
})
export class ReservedViewService {
  private readonly logger = injectLogger('ReservedViewService');

  constructor(
    private http: HttpClient,
    private connectRegionService: ConnectRegionService,
    private settingService: SettingService,
  ) {}

  getView(viewId: string): Observable<ViewData | undefined> {
    return this.getReservedView(viewId).pipe(
      catchError((err) => {
        if (this.isNotFound(err)) return this.getLegacyView(viewId);

        this.logger.error('Get reserved view failed', { viewId }, err);

        return of(undefined);
      }),
    );
  }

  putView(viewId: string, view: ViewData): Observable<ViewData> {
    return this.getReservedViewUrl(viewId).pipe(
      switchMap((url) => this.http.put<ViewData>(url, view)),
    );
  }

  deleteView(viewId: string): Observable<void> {
    const reservedDelete$ = this.deleteReservedView(viewId);
    const legacyDelete$ = this.deleteLegacyView(viewId);

    return forkJoin([reservedDelete$, legacyDelete$]).pipe(map(() => undefined));
  }

  private deleteReservedView(viewId: string): Observable<void> {
    return this.getReservedViewUrl(viewId).pipe(
      switchMap((url) => this.http.delete<void>(url)),
      catchError((err) => {
        if (!this.isNotFound(err)) {
          this.logger.error('Delete reserved view failed', { viewId }, err);
        }

        return of(undefined);
      }),
    );
  }

  private deleteLegacyView(viewId: string): Observable<void> {
    const settingKey = LEGACY_VIEW_SETTING_PREFIX + viewId;
    return this.settingService.deleteSetting(settingKey).pipe(
      catchError((err) => {
        if (!this.isNotFound(err)) {
          this.logger.error('Delete legacy view failed', { settingKey }, err);
        }

        return of(undefined);
      }),
    );
  }

  private getReservedView(viewId: string): Observable<ViewData> {
    return this.getReservedViewUrl(viewId).pipe(
      switchMap((url) => this.http.get<ReservedConnectView>(url)),
      map((reservedView) => reservedView.data),
    );
  }

  private getLegacyView(viewId: string): Observable<ViewData | undefined> {
    const settingKey = LEGACY_VIEW_SETTING_PREFIX + viewId;
    return this.settingService.getSetting<LegacyConnectView>(settingKey).pipe(
      map((setting) => this.mapLegacyView(setting.data)),
      catchError((err) => {
        if (!this.isNotFound(err)) {
          this.logger.error('Get legacy view failed', { settingKey }, err);
        }

        return of(undefined);
      }),
    );
  }

  private mapLegacyView(view: LegacyConnectView): ViewData {
    return {
      scans: view.scans ?? [],
      currentStation: view.currentStation,
    };
  }

  private getReservedViewUrl(viewId: string): Observable<string> {
    return this.connectRegionService
      .getConnectRegionEndpoint()
      .pipe(
        map((endpoint) =>
          buildUrl(endpoint.url, `views/${viewId}/reserved?app=${RESERVED_VIEW_APP}`),
        ),
      );
  }

  private isNotFound(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === HttpStatusCode.NotFound;
  }
}
